<template>
  <div ref="mediaWrapper" class="secured-media-wrapper" :class="['type-' + mediaType, { 'has-loaded': hasLoaded }]" @click="$emit('click')">
    <div v-if="isLoading || isProcessing" class="secured-media-loader d-flex flex-column justify-center align-center">
      <v-progress-circular indeterminate color="#58d388" size="22" width="2"></v-progress-circular>
      <span v-if="isProcessing" class="secured-media-status mt-2">Video wird verarbeitet ...</span>
    </div>

    <div v-else-if="!hasBeenVisible" class="secured-media-loader d-flex justify-center align-center">
      <v-icon color="rgba(255,255,255,0.3)">mdi-eye-off-outline</v-icon>
    </div>

    <img
      v-else-if="mediaType === 'image' && blobUrl"
      :src="blobUrl"
      class="secured-media-content"
      @load="handleContentLoaded"
      @error="handleContentError" />

    <iframe
      v-else-if="mediaType === 'video' && streamPlayback && streamUrl"
      :src="streamUrl"
      class="secured-media-content video"
      title="Geschütztes Akademie-Video"
      allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture"
      allowfullscreen
      @load="handleContentLoaded"
      @error="handleContentError"></iframe>

    <video
      v-else-if="mediaType === 'video' && !streamPlayback && blobUrl"
      :src="blobUrl"
      controls
      class="secured-media-content"
      @loadedmetadata="handleContentLoaded"
      @error="handleContentError"></video>

    <audio
      v-else-if="mediaType === 'audio' && blobUrl"
      :src="blobUrl"
      controls
      class="secured-media-content audio"
      @loadedmetadata="handleContentLoaded"
      @error="handleContentError"></audio>

    <iframe
      v-else-if="mediaType === 'pdf' && blobUrl"
      :src="blobUrl"
      class="secured-media-content pdf"
      title="Geschütztes PDF-Dokument"
      @load="handleContentLoaded"
      @error="handleContentError"></iframe>

    <a v-else-if="blobUrl" :href="blobUrl" :download="downloadName || 'datei'" class="secured-media-download">
      <v-icon left small>mdi-download</v-icon> Herunterladen
    </a>

    <div v-else-if="hasLoaded && !blobUrl && !streamUrl" class="secured-media-error">
      <v-icon small color="red lighten-2" class="mr-1">mdi-alert-circle</v-icon>
      <span class="caption red--text text--lighten-2">Fehler</span>
    </div>
  </div>
</template>

<script>
import axios from "axios"

const mediaCache = new Map()
const MAX_CACHED_FILE_BYTES = 8 * 1024 * 1024
const MAX_MEDIA_CACHE_BYTES = 24 * 1024 * 1024
let mediaCacheBytes = 0

function getCachedMedia(key) {
  const blob = mediaCache.get(key)
  if (!blob) return null
  mediaCache.delete(key)
  mediaCache.set(key, blob)
  return blob
}

function removeCachedMedia(key) {
  const blob = mediaCache.get(key)
  if (!blob) return
  mediaCache.delete(key)
  mediaCacheBytes = Math.max(0, mediaCacheBytes - blob.size)
}

function cacheMedia(key, blob) {
  if (!blob || blob.size > MAX_CACHED_FILE_BYTES) return
  removeCachedMedia(key)
  mediaCache.set(key, blob)
  mediaCacheBytes += blob.size
  while (mediaCacheBytes > MAX_MEDIA_CACHE_BYTES && mediaCache.size) {
    removeCachedMedia(mediaCache.keys().next().value)
  }
}

function buildHeaders() {
  return { Authorization: localStorage.getItem("token") }
}

export default {
  name: "SecuredMedia",
  props: {
    // Voll qualifizierter API-Pfad ab "/api/akademie/..." (ohne Token-Query)
    src: { type: String, required: true },
    mediaType: { type: String, default: "image" }, // image | video | audio | pdf | file
    downloadName: { type: String, default: null },
    eager: { type: Boolean, default: false }, // ignoriert IntersectionObserver
    cacheKey: { type: String, default: null }, // optional eigener Cache-Key
    cache: { type: Boolean, default: true },
    streamPlayback: { type: Boolean, default: false },
  },
  data() {
    return {
      isLoading: false,
      isProcessing: false,
      blobUrl: null,
      streamUrl: null,
      hasBeenVisible: false,
      hasLoaded: false,
      contentReady: false,
      observer: null,
      retryTimer: null,
    }
  },
  computed: {
    effectiveCacheKey() {
      return this.cacheKey || this.src
    },
  },
  watch: {
    src() {
      this.reloadMedia()
    },
    mediaType() {
      this.reloadMedia()
    },
    streamPlayback() {
      this.reloadMedia()
    },
  },
  mounted() {
    if (this.eager) {
      this.hasBeenVisible = true
      this.loadMedia()
      return
    }
    this.createObserver()
  },
  beforeDestroy() {
    this.disconnectObserver()
    this.clearRetryTimer()
    if (this.blobUrl) URL.revokeObjectURL(this.blobUrl)
  },
  methods: {
    reloadMedia() {
      this.reset()
      if (this.eager) {
        this.hasBeenVisible = true
        this.loadMedia()
      } else {
        this.$nextTick(this.createObserver)
      }
    },
    reset() {
      this.disconnectObserver()
      if (this.blobUrl) {
        URL.revokeObjectURL(this.blobUrl)
        this.blobUrl = null
      }
      this.streamUrl = null
      this.isLoading = false
      this.isProcessing = false
      this.hasLoaded = false
      this.contentReady = false
      this.hasBeenVisible = !!this.eager
      this.clearRetryTimer()
    },
    clearRetryTimer() {
      if (this.retryTimer) {
        clearTimeout(this.retryTimer)
        this.retryTimer = null
      }
    },
    retryPlayback(seconds = 10) {
      this.clearRetryTimer()
      this.isProcessing = true
      this.retryTimer = setTimeout(() => {
        this.retryTimer = null
        this.isProcessing = false
        this.hasLoaded = false
        this.loadMedia()
      }, Math.max(3, Number(seconds) || 10) * 1000)
    },
    disconnectObserver() {
      if (this.observer) {
        this.observer.disconnect()
        this.observer = null
      }
    },
    createObserver() {
      if (this.hasBeenVisible) return
      if (!this.$refs.mediaWrapper) {
        setTimeout(this.createObserver, 80)
        return
      }
      this.observer = new IntersectionObserver(this.handleIntersect, { threshold: 0.01, rootMargin: "100px" })
      this.observer.observe(this.$refs.mediaWrapper)
    },
    handleIntersect(entries) {
      if (entries[0].isIntersecting && !this.hasBeenVisible) {
        this.hasBeenVisible = true
        this.loadMedia()
        this.disconnectObserver()
      }
    },
    async loadMedia() {
      if (!this.src || this.hasLoaded) return
      if (this.mediaType === "video" && this.streamPlayback) {
        this.isLoading = true
        let retrying = false
        try {
          const res = await axios.get(this.src, { headers: buildHeaders() })
          if (res.status === 202) {
            retrying = true
            this.retryPlayback(res.headers["retry-after"])
            return
          }
          if (!res.data || !res.data.playerUrl) throw new Error("Cloudflare Player-URL fehlt")
          this.streamUrl = res.data.playerUrl
        } catch (err) {
          console.error("[SecuredMedia] playback error", err)
          this.$emit("error", err)
        } finally {
          this.isLoading = false
          this.hasLoaded = !retrying
        }
        return
      }
      const key = this.effectiveCacheKey
      const cached = this.cache ? getCachedMedia(key) : null
      if (cached) {
        this.blobUrl = URL.createObjectURL(cached)
        this.hasLoaded = true
        if (!["image", "video", "audio", "pdf"].includes(this.mediaType)) this.handleContentLoaded()
        return
      }
      this.isLoading = true
      try {
        const res = await axios.get(this.src, { responseType: "blob", headers: buildHeaders() })
        let blob = res.data
        if (!(blob instanceof Blob)) blob = new Blob([res.data], { type: res.headers["content-type"] })
        if (!blob.size) throw new Error("Leere Mediendatei")
        if (this.cache) cacheMedia(key, blob)
        this.blobUrl = URL.createObjectURL(blob)
        if (!["image", "video", "audio", "pdf"].includes(this.mediaType)) this.handleContentLoaded()
      } catch (err) {
        console.error("[SecuredMedia] load error", err)
        this.$emit("error", err)
      } finally {
        this.isLoading = false
        this.hasLoaded = true
      }
    },
    handleContentLoaded() {
      if (this.contentReady) return
      this.contentReady = true
      this.$emit("loaded")
    },
    handleContentError(error) {
      this.contentReady = false
      removeCachedMedia(this.effectiveCacheKey)
      if (this.blobUrl) URL.revokeObjectURL(this.blobUrl)
      this.blobUrl = null
      this.streamUrl = null
      this.$emit("error", error)
    },
  },
}
</script>

<style scoped>
.secured-media-wrapper {
  position: relative;
  display: block;
  width: 100%;
  min-height: 80px;
}
.secured-media-content {
  display: block;
  max-width: 100%;
  max-height: 100%;
  width: 100%;
  border-radius: 8px;
}
.secured-media-content.pdf {
  height: 70vh;
  border: 0;
}
.secured-media-content.video {
  aspect-ratio: 16 / 9;
  min-height: 220px;
  border: 0;
}
.secured-media-content.audio {
  height: 40px;
}
.secured-media-loader,
.secured-media-error {
  width: 100%;
  min-height: 80px;
  background: rgba(0, 0, 0, 0.25);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.secured-media-status {
  color: rgba(255, 255, 255, 0.78);
  font-size: 0.875rem;
}
.secured-media-download {
  display: inline-flex;
  align-items: center;
  color: #58d388;
  text-decoration: none;
  padding: 8px 12px;
  background: rgba(0, 150, 64, 0.1);
  border-radius: 8px;
}
</style>
